import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { IMaskDirective } from 'angular-imask';
import { forkJoin, merge, Subject, takeUntil, tap, timer } from 'rxjs';
import { nameOf } from '../../../../../core/functions/string-helpers';
import { Direction } from '../../../../../core/models/direction';
import { DMS } from '../../../../../core/models/dms';
import { DropdownOption } from '../../../../../core/models/dropdown-option';
import { FormGroupType } from '../../../../../core/models/form-group-type';
import { Parallel } from '../../../../../core/models/parallel';
import { GeoCoordinateSystemService } from '../../../../../core/services/geo-coordinate-system/geo-coordinate-system.service';
import { DropdownService } from '../../../../../core/services/resources/dropdown.service';
import { CoordinateIsInUSOrTerritoriesRequest } from '../../../../../core/services/validation/models/coordinate-is-in-us-or-territories-request';
import { ValidationResult } from '../../../../../core/services/validation/models/validation-result';
import { ValidationService } from '../../../../../core/services/validation/validation.service';
import { ApplicationFormFieldComponent } from '../../../../../shared/components/fcc-601/application-form-field/application-form-field.component';
import { ValidationErrorListComponent } from '../../../../../shared/components/validation-error-list/validation-error-list.component';
import { LatitudeLongitudeDms } from '../../../direct-entry/schedules-d-and-h/components/schedule-d-step-two/models/latitude-longitude-dms';
import {
    LatitudeDegreesControlValidators,
    LatitudeDirectionControlValidators,
    LatitudeMinutesControlValidators,
    LatitudeSecondsControlValidators,
    LongitudeDegreesControlValidators,
    LongitudeDirectionControlValidators,
    LongitudeMinutesControlValidators,
    LongitudeSecondsControlValidators,
} from './constants/latitude-longitude-control-validators';
import { LatitudeLongitudeHelperMessages } from './constants/latitude-longitude-helper-messages';
import { LatitudeLongitudeMaskConfig } from './constants/latitude-longitude-mask-config';

@Component({
    selector: 'app-latitude-longitude-form',
    imports: [
        CommonModule,
        ValidationErrorListComponent,
        MatCheckboxModule,
        MatFormFieldModule,
        ApplicationFormFieldComponent,
        MatSelectModule,
        ReactiveFormsModule,
        MatInputModule,
        IMaskDirective,
    ],
    templateUrl: './latitude-longitude-form.component.html',
})
export class LatitudeLongitudeFormComponent implements OnInit, OnDestroy {
    private readonly dropdownService = inject(DropdownService);
    private readonly coordinatesService = inject(GeoCoordinateSystemService);
    private readonly validationService = inject(ValidationService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    @Input() latitudeControl!: FormControl<string | null>;
    @Input() longitudeControl!: FormControl<string | null>;
    @Input() compact?: boolean = false;
    @Input() locationIsSavedToApplicationOrIsModification!: boolean;

    @ViewChild('toggleLatitudeLongitudeFormatElement', { static: false })
    toggleLatitudeLongitudeFormatElement: MatCheckbox | undefined;

    latitudeLongitudeInDecimalFormat: boolean = false;
    coordinateIsInUSOrTerritoriesResult: ValidationResult | null = null;
    latitudeDirectionOptions: DropdownOption[] = [];
    longitudeDirectionOptions: DropdownOption[] = [];
    messages = LatitudeLongitudeHelperMessages;
    latitudeLongitudeMaskConfig = LatitudeLongitudeMaskConfig;

    private destroy$ = new Subject<void>();

    /*
     * Form group for the latitude and longitude in DMS (Degree/Minutes/Seconds) format.
     * Form value will be converted to decimal format and set to the main form.
     */
    dmsForm: FormGroup<FormGroupType<LatitudeLongitudeDms>> = new FormGroup<
        FormGroupType<LatitudeLongitudeDms>
    >({
        latitudeDegrees: new FormControl(
            null,
            LatitudeDegreesControlValidators,
        ),
        latitudeMinutes: new FormControl(
            null,
            LatitudeMinutesControlValidators,
        ),
        latitudeSeconds: new FormControl(
            null,
            LatitudeSecondsControlValidators,
        ),
        latitudeDirection: new FormControl(
            Direction.North,
            LatitudeDirectionControlValidators,
        ),
        longitudeDegrees: new FormControl(
            null,
            LongitudeDegreesControlValidators,
        ),
        longitudeMinutes: new FormControl(
            null,
            LongitudeMinutesControlValidators,
        ),
        longitudeSeconds: new FormControl(
            null,
            LongitudeSecondsControlValidators,
        ),
        longitudeDirection: new FormControl(
            Direction.West,
            LongitudeDirectionControlValidators,
        ),
    });

    ngOnInit(): void {
        this.loadDropdownData();
        this.initializeDmsSyncWithDecimalControls();
        this.markDmsControlsAsTouchedOnParallelChangeHandler();

        if (this.locationIsSavedToApplicationOrIsModification) {
            this.dmsForm.markAllAsTouched();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleLatitudeLongitudeFormat(): void {
        this.latitudeLongitudeInDecimalFormat =
            !this.latitudeLongitudeInDecimalFormat;

        // Timer is used to allow the form controls to render before marking them.
        timer(0).subscribe(() => {
            this.markFormControlsFromLatitudeLongitudeValidationResult();
        });
    }

    getDegreesLabel(): string {
        return this.compact ? 'Deg' : 'Degrees';
    }

    getMinutesLabel(): string {
        return this.compact ? 'Min' : 'Minutes';
    }

    getSecondsLabel(): string {
        return this.compact ? 'Sec' : 'Seconds';
    }

    updateDmsLatLongFormControlsAndValidateCoordinates(
        parallel: Parallel,
    ): void {
        const control =
            parallel === 'latitude'
                ? this.latitudeControl
                : this.longitudeControl;

        if (control.value === '' || control.value == null) {
            control.patchValue(null, { emitEvent: false });
            this.coordinateIsInUSOrTerritoriesResult = null;

            // Clear any stale invalidCoordinate errors that may have been set on either coordinate.
            this.latitudeControl.updateValueAndValidity({ emitEvent: false });
            this.longitudeControl.updateValueAndValidity({ emitEvent: false });
            this.dmsForm.updateValueAndValidity({ emitEvent: false });
            return;
        }

        this.updateDmsLatitudeLongitudeValues(parallel);
        this.validateCoordinateIsInUSOrTerritories();
    }

    updateDecimalLatLongFormControlsAndValidateCoordinates(
        parallel: Parallel,
    ): void {
        const degreesControl = this.dmsForm.controls[`${parallel}Degrees`];
        const minutesControl = this.dmsForm.controls[`${parallel}Minutes`];
        const secondsControl = this.dmsForm.controls[`${parallel}Seconds`];

        // If all DMS values are cleared, clear the decimal coordinate + any stale validation result.
        if (
            degreesControl.value == null &&
            minutesControl.value == null &&
            secondsControl.value == null
        ) {
            const control =
                parallel === 'latitude'
                    ? this.latitudeControl
                    : this.longitudeControl;

            control.patchValue(null, { emitEvent: false });
            this.coordinateIsInUSOrTerritoriesResult = null;

            this.latitudeControl.updateValueAndValidity({ emitEvent: false });
            this.longitudeControl.updateValueAndValidity({ emitEvent: false });
            this.dmsForm.updateValueAndValidity({ emitEvent: false });
            return;
        }

        this.updateDecimalLatitudeLongitudeValues(parallel);
    }

    validateCoordinateIsInUSOrTerritories(): void {
        if (
            this.latitudeControl.value == null ||
            this.longitudeControl.value == null
        ) {
            return;
        }

        const request: CoordinateIsInUSOrTerritoriesRequest = {
            latitude: Number(this.latitudeControl.value),
            longitude: Number(this.longitudeControl.value),
        };

        this.validationService
            .coordinateIsInUSOrTerritories(request)
            .subscribe((result) => {
                this.coordinateIsInUSOrTerritoriesResult = result;
                this.markFormControlsFromLatitudeLongitudeValidationResult();

                // Ensuring that the view is updated after the validation result is processed.
                this.changeDetectorRef.detectChanges();
            });
    }

    private markDmsControlsAsTouchedOnParallelChangeHandler(): void {
        merge(
            this.dmsForm.controls.latitudeDegrees.valueChanges,
            this.dmsForm.controls.latitudeMinutes.valueChanges,
            this.dmsForm.controls.latitudeSeconds.valueChanges,
            this.dmsForm.controls.latitudeDirection.valueChanges,
        )
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.dmsForm.controls.latitudeDegrees.markAllAsTouched();
                    this.dmsForm.controls.latitudeMinutes.markAllAsTouched();
                    this.dmsForm.controls.latitudeSeconds.markAllAsTouched();
                    this.dmsForm.controls.latitudeDirection.markAllAsTouched();
                }),
            )
            .subscribe();

        merge(
            this.dmsForm.controls.longitudeDegrees.valueChanges,
            this.dmsForm.controls.longitudeMinutes.valueChanges,
            this.dmsForm.controls.longitudeSeconds.valueChanges,
            this.dmsForm.controls.longitudeDirection.valueChanges,
        )
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.dmsForm.controls.longitudeDegrees.markAllAsTouched();
                    this.dmsForm.controls.longitudeMinutes.markAllAsTouched();
                    this.dmsForm.controls.longitudeSeconds.markAllAsTouched();
                    this.dmsForm.controls.longitudeDirection.markAllAsTouched();
                }),
            )
            .subscribe();
    }

    private updateDecimalLatitudeLongitudeValues(parallel: Parallel): void {
        const degreesControl = this.dmsForm.controls[`${parallel}Degrees`];
        const minutesControl = this.dmsForm.controls[`${parallel}Minutes`];
        const secondsControl = this.dmsForm.controls[`${parallel}Seconds`];
        const directionControl = this.dmsForm.controls[`${parallel}Direction`];

        if (
            degreesControl.value == null &&
            minutesControl.value == null &&
            secondsControl.value == null
        ) {
            return;
        }

        const dms: DMS = {
            degrees: degreesControl.value ?? 0,
            minutes: minutesControl.value ?? 0,
            seconds: secondsControl.value ?? 0,
            direction: directionControl.value as Direction | undefined,
        };

        const control =
            parallel === 'latitude'
                ? this.latitudeControl
                : this.longitudeControl;

        // Mark dirty so navigating away triggers the save logic for the step.
        control.markAsDirty();
        control.setValue(
            String(this.coordinatesService.convertDMSToDecimal(dms)),
        );
    }

    private updateDmsLatitudeLongitudeValues(parallel: Parallel) {
        const control =
            parallel === 'latitude'
                ? this.latitudeControl
                : this.longitudeControl;

        if (control.value == null) {
            return;
        }

        const dms = this.coordinatesService.convertDecimalToDMS(
            Number(control.value),
            parallel,
        );

        this.dmsForm.patchValue(
            {
                [nameOf<LatitudeLongitudeDms>(`${parallel}Degrees`)]:
                    dms.degrees,
                [nameOf<LatitudeLongitudeDms>(`${parallel}Minutes`)]:
                    dms.minutes,
                [nameOf<LatitudeLongitudeDms>(`${parallel}Seconds`)]:
                    dms.seconds,
                [nameOf<LatitudeLongitudeDms>(`${parallel}Direction`)]:
                    dms.direction,
            },
            { emitEvent: false },
        );
    }

    private initializeDmsSyncWithDecimalControls(): void {
        this.updateDmsLatitudeLongitudeValues('latitude');
        this.updateDmsLatitudeLongitudeValues('longitude');

        // Validate the coordinates if both latitude and longitude have values during initialization.
        if (
            this.latitudeControl.value != null &&
            this.latitudeControl.value !== '' &&
            this.longitudeControl.value != null &&
            this.longitudeControl.value !== ''
        ) {
            this.validateCoordinateIsInUSOrTerritories();
        }

        // Keep DMS fields synchronized when decimal controls change programmatically (e.g. ASR prepopulation).
        merge(
            this.latitudeControl.valueChanges,
            this.longitudeControl.valueChanges,
        )
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateDmsLatitudeLongitudeValues('latitude');
                this.updateDmsLatitudeLongitudeValues('longitude');
            });
    }

    /**
     * Marks the latitude and longitude controls as invalid if the coordinate is not in the US or territories.
     * If the coordinate is valid, the errors are cleared.
     * @returns void
     */
    private markFormControlsFromLatitudeLongitudeValidationResult(): void {
        if (!this.coordinateIsInUSOrTerritoriesResult) {
            return;
        }

        if (this.coordinateIsInUSOrTerritoriesResult.isValid === false) {
            this.bulkSetLatitudeLongitudeErrorsAndMarkAsTouched({
                invalidCoordinate: true,
            });
        } else {
            this.bulkSetLatitudeLongitudeErrorsAndMarkAsTouched(null);
        }
    }

    /**
     * Sets errors on all the latitude and longitude controls (including the DMS form) and marks them as touched.
     * @param errors the errors to set on each control
     */
    private bulkSetLatitudeLongitudeErrorsAndMarkAsTouched(
        errors: { [key: string]: boolean } | null,
    ) {
        this.latitudeControl.setErrors(errors);
        this.longitudeControl.setErrors(errors);
        this.latitudeControl.markAsTouched();
        this.longitudeControl.markAsTouched();

        if (this.dmsForm.controls.latitudeDegrees.value !== null) {
            this.dmsForm.controls.latitudeDegrees.setErrors(errors);
        }
        if (this.dmsForm.controls.latitudeMinutes.value !== null) {
            this.dmsForm.controls.latitudeMinutes.setErrors(errors);
        }
        if (this.dmsForm.controls.latitudeSeconds.value !== null) {
            this.dmsForm.controls.latitudeSeconds.setErrors(errors);
        }
        if (this.dmsForm.controls.latitudeDirection.value !== null) {
            this.dmsForm.controls.latitudeDirection.setErrors(errors);
        }

        if (this.dmsForm.controls.longitudeDegrees.value !== null) {
            this.dmsForm.controls.longitudeDegrees.setErrors(errors);
        }
        if (this.dmsForm.controls.longitudeMinutes.value !== null) {
            this.dmsForm.controls.longitudeMinutes.setErrors(errors);
        }
        if (this.dmsForm.controls.longitudeSeconds.value !== null) {
            this.dmsForm.controls.longitudeSeconds.setErrors(errors);
        }
        if (this.dmsForm.controls.longitudeDirection.value !== null) {
            this.dmsForm.controls.longitudeDirection.setErrors(errors);
        }

        this.dmsForm.markAllAsTouched();
    }

    private loadDropdownData() {
        forkJoin({
            latitudeDirectionOptions:
                this.dropdownService.getLatitudeDirectionOptions(),
            longitudeDirectionOptions:
                this.dropdownService.getLongitudeDirectionOptions(),
        })
            .pipe(
                takeUntil(this.destroy$),
                tap(
                    ({
                        latitudeDirectionOptions: latitudeDirectionOptions,
                        longitudeDirectionOptions: longitudeDirectionOptions,
                    }) => {
                        this.latitudeDirectionOptions =
                            latitudeDirectionOptions;
                        this.longitudeDirectionOptions =
                            longitudeDirectionOptions;
                    },
                ),
            )
            .subscribe();
    }
}
